import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CodedError,
  type InterestTopic,
  type Locale,
  type PublicBroadcaster,
  type PublicCatalogue,
} from '@ebu/contracts';
import { fetchBroadcasters, fetchCatalogue } from '../../adapters/api-client';
import { pageIsLeaving } from '../../adapters/page-lifecycle';
import { LocalStore, type WorkspaceRecord } from '../../adapters/local-store';
import { useDiagnostics } from '../../diagnostics/context';
import { preferredLocale } from '../../i18n/i18n';
import {
  START_CHOICE,
  isSampleProfile,
  sampleLearning,
  sampleWorlds,
  type SampleProfileId,
} from '../treehouse/sample-learners';

/**
 * Guest session state: chosen broadcaster configuration, language, opt-in interests and the
 * public catalogue for that choice. There is no sign-in. Choices are written to the browser's
 * guest workspace (IndexedDB) only once the visitor actually changes something, so a first visit
 * stores nothing. Switching broadcaster re-queries the catalogue and never touches projects.
 *
 * Phase 9: the visitor may switch to a fictional sample learner (a workspace cloned into this
 * browser). Projects and Lab progress then belong to that workspace (`activeSampleId`) until they
 * go back to their own. Broadcaster, language and graphics stay the browser's own choices.
 */

export type Load<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; code: string };

export type StorageState = 'pending' | 'ready' | 'unavailable';
export type GraphicsMode = 'world' | 'map';
/** Who is playing (ADR 0017): "little" = ages 3–8, "bigger" = 9+. */
export type AgeMode = 'little' | 'bigger';
export type StoredScreenTime = WorkspaceRecord['screenTime'];

export interface GuestServices {
  idbFactory: IDBFactory | undefined;
  /** navigator.languages; used only to pick a sensible first language and broadcaster. */
  languages: readonly string[];
}

/** A fictional sample learner cloned into this browser. */
export interface SampleWorkspace {
  id: string;
  profile: SampleProfileId;
  createdAt: string;
}

/** UI texts for the cloned sample projects (they come from the UI language). */
export interface SampleTexts {
  firstRoom: string;
  nightLight: string;
  posterCaption: string;
}

export interface GuestState {
  broadcasters: Load<PublicBroadcaster[]>;
  broadcaster: PublicBroadcaster | null;
  /** Effective UI and content locale (requested locale if the broadcaster supports it). */
  locale: Locale;
  catalogue: Load<PublicCatalogue>;
  interests: InterestTopic[] | null;
  /** 3D island or the simple 2D map (low-graphics mode). */
  graphics: GraphicsMode;
  /** Who is playing; null until chosen on this browser. */
  ageMode: AgeMode | null;
  setAgeMode: (mode: AgeMode) => void;
  /** The grown-up's screen-time setting and today's use as last saved (read by ScreenTimeProvider). */
  storedScreenTime: StoredScreenTime;
  saveScreenTime: (value: NonNullable<StoredScreenTime>) => void;
  storage: StorageState;
  /** True once a guest workspace exists in this browser. */
  savedChoices: boolean;
  projectCount: number;
  setBroadcaster: (id: string) => void;
  setLocale: (locale: Locale) => void;
  setInterests: (interests: InterestTopic[] | null) => void;
  setGraphics: (mode: GraphicsMode) => void;
  /** The island welcome is shown once per visit (kept in memory only). */
  welcomeSeen: boolean;
  dismissWelcome: () => void;
  clearLocalData: () => Promise<boolean>;
  /** Increases after "Clear my data", so other local features drop what they hold in memory. */
  dataEpoch: number;
  retry: () => void;
  /** Sample learner workspaces on this browser, and the one in use (null = the visitor's own). */
  samples: readonly SampleWorkspace[];
  activeSample: SampleWorkspace | null;
  /** Opens the profile's copy, cloning it first if this browser has none. False on failure. */
  startSample: (profile: SampleProfileId, texts: SampleTexts) => Promise<boolean>;
  /** Removes the copy and makes a fresh one from the seed. */
  resetSample: (profile: SampleProfileId, texts: SampleTexts) => Promise<boolean>;
  removeSample: (profile: SampleProfileId) => Promise<boolean>;
  leaveSample: () => Promise<boolean>;
}

interface Keyed<T> {
  key: string;
  load: Load<T>;
}

const LOADING = { status: 'loading' } as const;

function resultFor<T>(result: Keyed<T> | null, key: string): Load<T> {
  return result !== null && result.key === key ? result.load : LOADING;
}

const GuestContext = createContext<GuestState | null>(null);
const GuestServicesContext = createContext<GuestServices | null>(null);

/** Browser services (IndexedDB factory, languages) for features with their own local storage. */
export function useGuestServices(): GuestServices {
  const services = useContext(GuestServicesContext);
  if (!services) throw new Error('useGuestServices must be used inside <GuestProvider>');
  return services;
}

export function useGuest(): GuestState {
  const state = useContext(GuestContext);
  if (!state) throw new Error('useGuest must be used inside <GuestProvider>');
  return state;
}

function toSamples(records: readonly WorkspaceRecord[]): SampleWorkspace[] {
  return records.flatMap((r) =>
    isSampleProfile(r.sampleProfileId)
      ? [{ id: r.id, profile: r.sampleProfileId, createdAt: r.createdAt }]
      : [],
  );
}

function codeOf(error: unknown): string {
  return error instanceof CodedError ? error.code : 'WEB-API-001';
}

function effectiveLocale(broadcaster: PublicBroadcaster | null, requested: Locale): Locale {
  if (!broadcaster) return requested;
  return broadcaster.supportedLocales.includes(requested) ? requested : broadcaster.defaultLocale;
}

/** Stored choice if still offered, else the broadcaster for the browser language, else the first. */
export function chooseBroadcaster(
  list: readonly PublicBroadcaster[],
  storedId: string | null,
  requested: Locale,
): PublicBroadcaster | null {
  return (
    list.find((b) => b.id === storedId) ??
    list.find((b) => b.defaultLocale === requested) ??
    list[0] ??
    null
  );
}

export function GuestProvider({
  services,
  children,
}: {
  services: GuestServices;
  children: ReactNode;
}) {
  const logger = useDiagnostics();
  // Async results are stored with the request they answer; anything else reads as "loading".
  const [broadcasterResult, setBroadcasterResult] = useState<Keyed<PublicBroadcaster[]> | null>(
    null,
  );
  const [catalogueResult, setCatalogueResult] = useState<Keyed<PublicCatalogue> | null>(null);
  const [storage, setStorage] = useState<StorageState>('pending');
  const [workspace, setWorkspace] = useState<WorkspaceRecord | null>(null);
  const [projectCount, setProjectCount] = useState(0);
  const [samples, setSamples] = useState<SampleWorkspace[]>([]);
  const [requestedLocale, setRequestedLocale] = useState<Locale>(() =>
    preferredLocale(services.languages),
  );
  const [broadcasterId, setBroadcasterId] = useState<string | null>(null);
  const [interests, setInterestsState] = useState<InterestTopic[] | null>(null);
  const [graphics, setGraphicsState] = useState<GraphicsMode>('world');
  const [ageMode, setAgeModeState] = useState<AgeMode | null>(null);
  const [storedScreenTime, setStoredScreenTime] = useState<StoredScreenTime>(null);
  const [welcomeSeen, setWelcomeSeen] = useState(false);
  const [dataEpoch, setDataEpoch] = useState(0);
  const dismissWelcome = useCallback(() => setWelcomeSeen(true), []);
  const [attempt, setAttempt] = useState(0);
  const store = useRef<LocalStore | null>(null);
  // Resolves once the stored workspace has been read (or storage failed), so a choice made in the
  // first moments is written after that read and is not overwritten by older stored values.
  const [storageSettled] = useState(() => {
    let resolve: () => void = () => undefined;
    const promise = new Promise<void>((r) => {
      resolve = r;
    });
    return { promise, resolve };
  });
  const touched = useRef(
    new Set<'locale' | 'broadcasterId' | 'interests' | 'graphics' | 'ageMode' | 'screenTime'>(),
  );

  // Local storage: read the existing workspace (never create one just by visiting).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const opened = await LocalStore.open({ idbFactory: services.idbFactory });
        if (cancelled) {
          opened.close();
          return;
        }
        store.current = opened;
        const existing = await opened.getGuestWorkspace();
        if (cancelled) return;
        if (existing) {
          const keep = touched.current;
          const cloned = toSamples(await opened.listSampleWorkspaces());
          if (cancelled) return;
          setWorkspace(existing);
          setSamples(cloned);
          if (!keep.has('locale')) setRequestedLocale(existing.locale);
          if (!keep.has('broadcasterId')) setBroadcasterId(existing.broadcasterId);
          if (!keep.has('interests')) setInterestsState(existing.interests);
          if (!keep.has('graphics')) setGraphicsState(existing.graphics);
          if (!keep.has('ageMode')) setAgeModeState(existing.ageMode);
          if (!keep.has('screenTime')) setStoredScreenTime(existing.screenTime);
          const owner = cloned.find((c) => c.id === existing.activeSampleId)?.id ?? existing.id;
          setProjectCount((await opened.listProjects(owner)).projects.length);
        }
        setStorage('ready');
        storageSettled.resolve();
      } catch (error) {
        storageSettled.resolve();
        if (cancelled) return;
        logger.warn('guest', 'Local storage unavailable; choices kept in memory only', {
          code: 'WEB-STORAGE-001',
          origin: 'apps/web/src/features/guest/guest-state.tsx:GuestProvider',
          context: { err: error },
        });
        setStorage('unavailable');
      }
    })();
    return () => {
      cancelled = true;
      store.current?.close();
      store.current = null;
    };
  }, [services.idbFactory, logger, storageSettled]);

  // Broadcaster configurations.
  useEffect(() => {
    const controller = new AbortController();
    const key = String(attempt);
    fetchBroadcasters({ signal: controller.signal }).then(
      (list) => setBroadcasterResult({ key, load: { status: 'ready', data: list } }),
      (error: unknown) => {
        if (controller.signal.aborted || pageIsLeaving()) return;
        const code = codeOf(error);
        logger.error('guest', 'Broadcaster list could not be loaded', {
          code: 'WEB-API-001',
          origin: 'apps/web/src/features/guest/guest-state.tsx:GuestProvider',
          context: { serverCode: code, err: error },
        });
        setBroadcasterResult({ key, load: { status: 'error', code } });
      },
    );
    return () => controller.abort();
  }, [attempt, logger]);

  const broadcasters = resultFor(broadcasterResult, String(attempt));
  const broadcaster =
    broadcasters.status === 'ready'
      ? chooseBroadcaster(broadcasters.data, broadcasterId, requestedLocale)
      : null;
  const locale = effectiveLocale(broadcaster, requestedLocale);
  const activeId = broadcaster?.id ?? null;
  const catalogueKey = `${activeId ?? ''}|${locale}|${attempt}`;

  // Catalogue for the active broadcaster + locale; waits for storage so the first query uses the
  // stored choice instead of fetching a default catalogue first.
  useEffect(() => {
    if (storage === 'pending' || activeId === null) return;
    const controller = new AbortController();
    fetchCatalogue(activeId, locale, { signal: controller.signal }).then(
      (data) => setCatalogueResult({ key: catalogueKey, load: { status: 'ready', data } }),
      (error: unknown) => {
        if (controller.signal.aborted || pageIsLeaving()) return;
        const code = codeOf(error);
        logger.error('catalogue', 'Public catalogue could not be loaded', {
          code: 'WEB-API-001',
          origin: 'apps/web/src/features/guest/guest-state.tsx:GuestProvider',
          context: { serverCode: code, broadcasterId: activeId, locale, err: error },
        });
        setCatalogueResult({ key: catalogueKey, load: { status: 'error', code } });
      },
    );
    return () => controller.abort();
  }, [activeId, locale, storage, catalogueKey, logger]);

  let catalogue: Load<PublicCatalogue>;
  if (broadcasters.status === 'error') catalogue = broadcasters;
  else if (broadcasters.status === 'ready' && activeId === null) {
    catalogue = { status: 'error', code: 'CONTENT-CONFIG-001' };
  } else catalogue = resultFor(catalogueResult, catalogueKey);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  /** Writes choices to the guest workspace, creating it on the first real choice. */
  const persist = useCallback(
    async (patch: {
      locale?: Locale;
      broadcasterId?: string;
      interests?: InterestTopic[] | null;
      graphics?: GraphicsMode;
      ageMode?: AgeMode;
      screenTime?: StoredScreenTime;
    }) => {
      for (const key of Object.keys(patch)) touched.current.add(key as 'locale');
      await storageSettled.promise;
      const opened = store.current;
      if (!opened) return;
      try {
        const current = await opened.getOrCreateGuestWorkspace(patch.locale ?? requestedLocale);
        setWorkspace(await opened.updateWorkspace(current.id, patch));
      } catch (error) {
        logger.warn('guest', 'Could not save a guest choice; kept in memory only', {
          code: 'WEB-STORAGE-001',
          origin: 'apps/web/src/features/guest/guest-state.tsx:persist',
          context: { fields: Object.keys(patch).join(','), err: error },
        });
        setStorage('unavailable');
      }
    },
    [logger, requestedLocale, storageSettled],
  );

  const setBroadcaster = useCallback(
    (id: string) => {
      setBroadcasterId(id);
      logger.info('guest', 'Broadcaster switched', { context: { broadcasterId: id } });
      void persist({ broadcasterId: id, locale: requestedLocale });
    },
    [logger, persist, requestedLocale],
  );

  const setLocale = useCallback(
    (next: Locale) => {
      setRequestedLocale(next);
      void persist({
        locale: next,
        ...(activeId !== null ? { broadcasterId: activeId } : {}),
      });
    },
    [persist, activeId],
  );

  const setInterests = useCallback(
    (next: InterestTopic[] | null) => {
      setInterestsState(next);
      void persist({ interests: next });
    },
    [persist],
  );

  const setGraphics = useCallback(
    (next: GraphicsMode) => {
      setGraphicsState(next);
      void persist({ graphics: next });
    },
    [persist],
  );

  const setAgeMode = useCallback(
    (next: AgeMode) => {
      setAgeModeState(next);
      void persist({ ageMode: next });
    },
    [persist],
  );

  const saveScreenTime = useCallback(
    (next: NonNullable<StoredScreenTime>) => {
      setStoredScreenTime(next);
      void persist({ screenTime: next });
    },
    [persist],
  );

  const clearLocalData = useCallback(async () => {
    const opened = store.current;
    if (!opened) return false;
    try {
      await opened.clearAllLocalData();
      setDataEpoch((n) => n + 1);
      setWorkspace(null);
      setSamples([]);
      setProjectCount(0);
      setInterestsState(null);
      setAgeModeState(null);
      setStoredScreenTime(null);
      logger.info('guest', 'Local data cleared by the visitor');
      return true;
    } catch (error) {
      logger.error('guest', 'Clear my data failed', {
        code: 'WEB-STORAGE-001',
        origin: 'apps/web/src/features/guest/guest-state.tsx:clearLocalData',
        context: { err: error },
      });
      return false;
    }
  }, [logger]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const activeSample = samples.find((c) => c.id === workspace?.activeSampleId) ?? null;

  /** Runs a sample-workspace change and refreshes the list and the active pointer. */
  const sampleChange = useCallback(
    async (message: string, work: (s: LocalStore, guestId: string) => Promise<string | null>) => {
      await storageSettled.promise;
      const opened = store.current;
      if (!opened) return false;
      try {
        const guest = await opened.getOrCreateGuestWorkspace(requestedLocale);
        const active = await work(opened, guest.id);
        const next = await opened.updateWorkspace(guest.id, { activeSampleId: active });
        const cloned = toSamples(await opened.listSampleWorkspaces());
        setSamples(cloned);
        setWorkspace(next);
        setProjectCount((await opened.listProjects(active ?? guest.id)).projects.length);
        return true;
      } catch (error) {
        logger.warn('guest', message, {
          code: 'WEB-STORAGE-001',
          origin: 'apps/web/src/features/guest/guest-state.tsx:sampleChange',
          context: { err: error },
        });
        return false;
      }
    },
    [logger, requestedLocale, storageSettled],
  );

  const cloneSample = useCallback(
    async (opened: LocalStore, profile: SampleProfileId, texts: SampleTexts) =>
      (
        await opened.cloneSampleWorkspace({
          sampleProfileId: profile,
          locale: requestedLocale,
          startChoice: START_CHOICE[profile],
          learning: (id) => sampleLearning(profile, id),
          worlds: sampleWorlds(profile, texts),
        })
      ).id,
    [requestedLocale],
  );

  const startSample = useCallback(
    (profile: SampleProfileId, texts: SampleTexts) =>
      sampleChange('Sample learner could not be opened', async (opened) => {
        const existing = toSamples(await opened.listSampleWorkspaces()).find(
          (c) => c.profile === profile,
        );
        return existing ? existing.id : cloneSample(opened, profile, texts);
      }),
    [sampleChange, cloneSample],
  );

  const resetSample = useCallback(
    (profile: SampleProfileId, texts: SampleTexts) =>
      sampleChange('Sample learner could not be started again', async (opened) => {
        for (const c of toSamples(await opened.listSampleWorkspaces())) {
          if (c.profile === profile) await opened.deleteSampleWorkspace(c.id);
        }
        return cloneSample(opened, profile, texts);
      }),
    [sampleChange, cloneSample],
  );

  const removeSample = useCallback(
    (profile: SampleProfileId) =>
      sampleChange('Sample learner could not be removed', async (opened, guestId) => {
        const guest = await opened.getWorkspace(guestId);
        let active = guest?.activeSampleId ?? null;
        for (const c of toSamples(await opened.listSampleWorkspaces())) {
          if (c.profile !== profile) continue;
          await opened.deleteSampleWorkspace(c.id);
          if (active === c.id) active = null;
        }
        return active;
      }),
    [sampleChange],
  );

  const leaveSample = useCallback(
    () => sampleChange('Could not go back to your own workspace', () => Promise.resolve(null)),
    [sampleChange],
  );

  const value = useMemo<GuestState>(
    () => ({
      broadcasters,
      broadcaster,
      locale,
      catalogue,
      interests,
      graphics,
      ageMode,
      setAgeMode,
      storedScreenTime,
      saveScreenTime,
      storage,
      savedChoices: workspace !== null,
      projectCount,
      setBroadcaster,
      setLocale,
      setInterests,
      setGraphics,
      welcomeSeen,
      dismissWelcome,
      clearLocalData,
      dataEpoch,
      retry,
      samples,
      activeSample,
      startSample,
      resetSample,
      removeSample,
      leaveSample,
    }),
    [
      broadcasters,
      broadcaster,
      locale,
      catalogue,
      interests,
      graphics,
      ageMode,
      setAgeMode,
      storedScreenTime,
      saveScreenTime,
      storage,
      workspace,
      projectCount,
      setBroadcaster,
      setLocale,
      setInterests,
      setGraphics,
      welcomeSeen,
      dismissWelcome,
      clearLocalData,
      dataEpoch,
      retry,
      samples,
      activeSample,
      startSample,
      resetSample,
      removeSample,
      leaveSample,
    ],
  );

  return (
    <GuestServicesContext.Provider value={services}>
      <GuestContext.Provider value={value}>{children}</GuestContext.Provider>
    </GuestServicesContext.Provider>
  );
}
